import {
  Box,
  Button,
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Stack,
  useDialogController,
} from '@ff-it/ui';
import { type ReactElement } from 'react';
import type { TaskList, ListTask, TaskResponse } from '../types';
import { Form, Submit, useSubmitHandler } from '@ff-it/form';
import { toast } from 'react-toastify';
import { CommentStream } from 'modules/comments/CommentSream';
import { useAnchorId } from 'modules/comments/useNavigationState';
import { TaskForm } from './TaskForm';
import { EmbeddedUser } from 'modules/core/types';
import { KeyedMutator } from 'swr';
import { updateFormContainer } from './styles.css';
import { getUserTitle } from 'modules/core/helpers';
import { actionErrorOrThrow, formatDateTime } from 'utilities';
import { fetcher } from 'services';
import { createConfirmDialog } from 'components';
import { FeaturedIcon } from 'components/Icon';
import { useNotificationSeen } from 'modules/notifications';

type DailogFormProps = {
  url: string;
  task: ListTask;
  assigneeOptions: EmbeddedUser[];
  onTaskListUpdate: (taskList: TaskList) => void;
  mutate: KeyedMutator<ListTask[]>;
};

const areYouSure = createConfirmDialog();
function DialogForm({ url, task, assigneeOptions, onTaskListUpdate, mutate }: DailogFormProps): ReactElement {
  useNotificationSeen(`tasks:task:${task.id}`);

  const dialog = useDialogController();
  const onSubmit = useSubmitHandler(
    { url: `${url}${task.id}/`, method: 'PUT' },
    {
      onSuccess: (response: TaskResponse) => {
        mutate((items) => items?.map((item) => (item.id === task.id ? response.task : item)));
        onTaskListUpdate(response.task_list);
        dialog.setOpen(false);
        toast.success('Task has been updated.', { autoClose: 3000 });
      },
    },
  );

  const onDelete = async () => {
    if (!(await areYouSure())) {
      return;
    }
    try {
      const response = await fetcher<{ task_list: TaskList }>({ url: `${url}${task.id}/`, method: 'DELETE' });
      mutate((items) => items?.filter((item) => item.id !== task.id));
      onTaskListUpdate(response.task_list);
      dialog.setOpen(false);
      toast.warning('Task has been deleted.', { autoClose: 3000 });
    } catch (e: any) {
      actionErrorOrThrow(e);
    }
  };

  const commentId = useAnchorId();
  return (
    <Stack direction="horizontal" height="full" space="0">
      <Form onSubmit={onSubmit} initialValues={task} className={updateFormContainer}>
        <DialogHeader title="Task">
          <FeaturedIcon icon="circle-check" ml="auto" />
        </DialogHeader>
        <DialogBody>
          <Stack direction="horizontal" textSize="sm" color="secondary" justify="between" mb="4">
            <Box>Created by {getUserTitle(task.created_by)}</Box>
            <Box>{formatDateTime(task.created_at)}</Box>
          </Stack>
          <TaskForm assigneeOptions={assigneeOptions} />
        </DialogBody>
        <DialogFooter mt="auto">
          <Submit />
          <DialogClose className="ml-auto" />
          <Button type="button" variant="danger" ml="1" onClick={onDelete} testId="remove-task">
            Delete
          </Button>
        </DialogFooter>
      </Form>
      <CommentStream
        commentId={commentId}
        topic={task.topic}
        onTopicUpdate={(topic) => {
          mutate((items) => items?.map((item) => (item.id === task.id ? { ...item, topic } : item)));
        }}
      />
    </Stack>
  );
}

type UpdateTaskDialogProps = Omit<DailogFormProps, 'task'> & {
  task: ListTask | null;
  onClose: () => void;
};

export function UpdateTaskDialog({ task, onClose, ...rest }: UpdateTaskDialogProps): ReactElement {
  return (
    <Dialog
      open={task !== null}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent
        testId="update-task"
        right
        display="flex"
        flexDirection="column"
        overflow="hidden"
        p="0"
        gap="0"
        initialFocus={-1}
      >
        {task && <DialogForm task={task} {...rest} />}
      </DialogContent>
    </Dialog>
  );
}
